import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

// Single source of truth for CRM vocabulary in the UI. These lists used to be
// hardcoded separately in ~12 files that disagreed with each other; they now
// come from the org's configurable master data via /crm-config/*.

export interface LeadSourceOption {
  id: string
  name: string
  color?: string
  position?: number
}

export interface LeadStatusOption {
  id: string
  name: string
  color?: string
  position?: number
  isWon?: boolean
  isLost?: boolean
}

export interface DealStageOption {
  id: string
  name: string
  color?: string
  position?: number
  isWon?: boolean
  isLost?: boolean
  defaultProbability?: number
}

function normalize<T>(rows: any[]): T[] {
  return rows.map((r) => ({ ...r, id: r.id ?? r._id })) as T[]
}

export function useLeadSources() {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['crm-config', 'lead-sources', organization?.id],
    queryFn: async () => {
      const res = await api.get<any>('/crm-config/lead-sources', { organizationId: organization?.id })
      return normalize<LeadSourceOption>(res?.data || [])
    },
    enabled: !!organization?.id && can('LEADS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useLeadStatuses() {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['crm-config', 'lead-statuses', organization?.id],
    queryFn: async () => {
      const res = await api.get<any>('/crm-config/lead-statuses', { organizationId: organization?.id })
      return normalize<LeadStatusOption>(res?.data || [])
    },
    enabled: !!organization?.id && can('LEADS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useDealStages() {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['crm-config', 'deal-stages', organization?.id],
    queryFn: async () => {
      const res = await api.get<any>('/crm-config/deal-stages', { organizationId: organization?.id })
      return normalize<DealStageOption>(res?.data || [])
    },
    enabled: !!organization?.id && can('DEALS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

// Create/update/delete for each vocabulary, mirroring the backend's own
// generic VOCABULARIES design (crm-vocabulary.ts) — one parameterized
// factory instead of three hand-copied mutation sets.
type VocabPath = 'lead-sources' | 'lead-statuses' | 'deal-stages'

function useVocabMutations(path: VocabPath, queryKeyPart: string) {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = ['crm-config', queryKeyPart, organization?.id]
  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const create = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      api.post<any>(`/crm-config/${path}`, { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>
      api.patch<any>(`/crm-config/${path}/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/crm-config/${path}/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, remove }
}

export function useLeadSourceMutations() {
  return useVocabMutations('lead-sources', 'lead-sources')
}

export function useLeadStatusMutations() {
  return useVocabMutations('lead-statuses', 'lead-statuses')
}

export function useDealStageMutations() {
  return useVocabMutations('deal-stages', 'deal-stages')
}

export interface ServiceOption {
  id: string
  name: string
  category: string
  description?: string
  amount: number
  position?: number
}

export interface ServiceDraft {
  id?: string
  name: string
  category: string
  description: string
  amount: number
}

const EMPTY_SERVICE_DRAFT: ServiceDraft = { name: '', category: 'TECHNOLOGY', description: '', amount: 0 }

function normalizeServices(rows: any[]): ServiceOption[] {
  return rows.map((r) => ({ ...r, id: r.id ?? r._id })) as ServiceOption[]
}

export function useServices() {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['crm-config', 'services', organization?.id],
    queryFn: async () => {
      const res = await api.get<any>('/services', { organizationId: organization?.id })
      return normalizeServices(res?.data || [])
    },
    enabled: !!organization?.id,
    staleTime: 5 * 60 * 1000,
  })
}

export function useServiceMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = ['crm-config', 'services', organization?.id]
  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const create = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      api.post<any>('/services', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>
      api.patch<any>(`/services/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/services/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, remove }
}

// ============================================
// ASSETS - PHYSICAL & DIGITAL
// ============================================

export type AssetType = 'PHYSICAL' | 'DIGITAL'

export type PhysicalAssetCategory = 
  | 'LAPTOP'
  | 'DESKTOP_PC'
  | 'MONITOR'
  | 'KEYBOARD'
  | 'MOUSE'
  | 'HEADSET'
  | 'WEBCAM'
  | 'TABLET'
  | 'MOBILE_PHONE'
  | 'SERVER'
  | 'NETWORKING_EQUIPMENT'
  | 'UPS'
  | 'OFFICE_CHAIR'
  | 'OFFICE_DESK'
  | 'CONFERENCE_TABLE'
  | 'CABINET'
  | 'PRINTER'
  | 'SCANNER'
  | 'PROJECTOR'
  | 'PHOTOCOPIER'
  | 'AIR_CONDITIONER'
  | 'VEHICLE'
  | 'ELECTRICAL_EQUIPMENT'
  | 'OTHER'
export type DigitalAssetCategory = 'BRAND_ASSETS' | 'SALES_DOCUMENTS' | 'LEGAL_COMPLIANCE' | 'MARKETING' | 'COMPANY_DOCUMENTS'
export type AssetCategory = PhysicalAssetCategory | DigitalAssetCategory

export type PhysicalAssetStatus = 'AVAILABLE' | 'ASSIGNED' | 'UNDER_MAINTENANCE' | 'DAMAGED' | 'LOST' | 'RETIRED' | 'DISPOSED'
export type DigitalAssetStatus = 'ACTIVE' | 'ARCHIVED' | 'EXPIRED'
export type AssetStatus = PhysicalAssetStatus | DigitalAssetStatus

export type WarrantyStatus = 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED' | 'NO_WARRANTY'
export type MaintenanceType = 'PREVENTIVE' | 'CORRECTIVE' | 'UPGRADE' | 'CALIBRATION' | 'OTHER'

export type AssetHistoryAction =
  | 'CREATED'
  | 'ASSIGNED'
  | 'RETURNED'
  | 'TRANSFERRED'
  | 'LOCATION_CHANGED'
  | 'DEPARTMENT_CHANGED'
  | 'STATUS_CHANGED'
  | 'MAINTENANCE_SENT'
  | 'MAINTENANCE_RETURNED'
  | 'RETIRED'
  | 'DISPOSED'
  | 'UPDATED'
  | 'DOCUMENT_ADDED'
  | 'DOCUMENT_REMOVED'
  | 'WARRANTY_UPDATED'

export interface PhysicalAssetData {
  assetCode: string
  serialNumber?: string
  brand?: string
  model?: string
  purchaseDate?: string
  purchasePrice?: number
  currentValue?: number
  vendor?: string
  invoiceNumber?: string
  purchaseOrderNumber?: string
  assignedTo?: string
  assignedToName?: string
  department?: string
  location?: string
  assignmentDate?: string
  status: PhysicalAssetStatus
  warrantyStartDate?: string
  warrantyEndDate?: string
  warrantyProvider?: string
  notes?: string
  imageUrl?: string
  warrantyStatus?: WarrantyStatus
}

export interface DigitalAssetData {
  fileUrl: string
  fileName: string
  fileType: string
  fileSize: number
  tags?: string[]
  expiryDate?: string
  version?: number
}

export interface AssetOption {
  id: string
  organizationId: string
  assetType: AssetType
  name: string
  category: AssetCategory
  description?: string
  physicalData?: PhysicalAssetData
  digitalData?: DigitalAssetData
  // Flat properties from API response (for backwards compatibility)
  fileUrl?: string
  fileName?: string
  fileType?: string
  fileSize?: number
  tags?: string[]
  expiryDate?: string
  version?: number
  status: AssetStatus
  createdBy: string
  createdAt: string
  updatedAt: string
  warrantyStatus?: WarrantyStatus
}

export interface AssetDraft {
  id?: string
  assetType: AssetType
  name: string
  category: AssetCategory
  description?: string
  physicalData?: Partial<PhysicalAssetData>
  digitalData?: Partial<DigitalAssetData>
  // Flat properties for form handling (matches API response format)
  fileUrl?: string
  fileName?: string
  fileType?: string
  fileSize?: number
  tags?: string[]
  expiryDate?: string
  version?: number
  status?: AssetStatus
}

export interface AssetHistoryOption {
  id: string
  assetId: string
  action: AssetHistoryAction
  fieldName?: string
  previousValue?: string
  newValue?: string
  performedBy: string
  performedByName?: string
  notes?: string
  metadata?: Record<string, any>
  createdAt: string
}

export interface MaintenanceOption {
  id: string
  assetId: string
  maintenanceDate: string
  maintenanceType: MaintenanceType
  vendor?: string
  cost?: number
  description: string
  nextMaintenanceDate?: string
  documentUrls?: string[]
  documentNames?: string[]
  performedBy: string
  performedByName?: string
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
  createdAt: string
}

export type AssetDocumentType =
  | 'PURCHASE_INVOICE'
  | 'WARRANTY_CARD'
  | 'AMC'
  | 'REPAIR_INVOICE'
  | 'DELIVERY_CHALLAN'
  | 'ASSET_PHOTOGRAPH'
  | 'OTHER'

export interface AssetDocumentOption {
  id: string
  assetId: string
  organizationId: string
  documentType: AssetDocumentType
  fileName: string
  fileUrl: string
  fileType: string
  fileSize: number
  uploadedBy: string
  uploadedByName?: string
  uploadedAt: string
  description?: string
}

export interface AssetSummary {
  total: number
  physical: {
    total: number
    available: number
    assigned: number
    underMaintenance: number
    damaged: number
    lost: number
    retired: number
    disposed: number
  }
  digital: {
    total: number
    active: number
    archived: number
    expired: number
  }
}

const PHYSICAL_CATEGORY_LABELS: Record<PhysicalAssetCategory, string> = {
  // IT & Computing
  LAPTOP: 'Laptop',
  DESKTOP_PC: 'Desktop PC',
  MONITOR: 'Monitor',
  KEYBOARD: 'Keyboard',
  MOUSE: 'Mouse',
  HEADSET: 'Headset',
  WEBCAM: 'Webcam',
  TABLET: 'Tablet',
  MOBILE_PHONE: 'Mobile Phone',
  // Server & Network
  SERVER: 'Server',
  NETWORKING_EQUIPMENT: 'Networking Equipment',
  UPS: 'UPS',
  // Office Furniture
  OFFICE_CHAIR: 'Office Chair',
  OFFICE_DESK: 'Office Desk',
  CONFERENCE_TABLE: 'Conference Table',
  CABINET: 'Cabinet',
  // Office Equipment
  PRINTER: 'Printer',
  SCANNER: 'Scanner',
  PROJECTOR: 'Projector',
  PHOTOCOPIER: 'Photocopier',
  // Facilities
  AIR_CONDITIONER: 'Air Conditioner',
  // Vehicles
  VEHICLE: 'Vehicle',
  // Electrical
  ELECTRICAL_EQUIPMENT: 'Electrical Equipment',
  // Other
  OTHER: 'Other',
}

const DIGITAL_CATEGORY_LABELS: Record<DigitalAssetCategory, string> = {
  BRAND_ASSETS: 'Brand Assets',
  SALES_DOCUMENTS: 'Sales & Documents',
  LEGAL_COMPLIANCE: 'Legal & Compliance',
  MARKETING: 'Marketing',
  COMPANY_DOCUMENTS: 'Company Documents',
}

const CATEGORY_LABELS: Record<string, string> = {
  ...PHYSICAL_CATEGORY_LABELS,
  ...DIGITAL_CATEGORY_LABELS,
}

const CATEGORY_ICONS: Record<AssetCategory, string> = {
  // IT & Computing
  LAPTOP: 'Laptop',
  DESKTOP_PC: 'Monitor',
  MONITOR: 'Monitor',
  KEYBOARD: 'Keyboard',
  MOUSE: 'Mouse',
  HEADSET: 'Headphones',
  WEBCAM: 'Video',
  TABLET: 'Tablet',
  MOBILE_PHONE: 'Smartphone',
  // Server & Network
  SERVER: 'Server',
  NETWORKING_EQUIPMENT: 'Wifi',
  UPS: 'Battery',
  // Office Furniture
  OFFICE_CHAIR: 'Chair',
  OFFICE_DESK: 'Layout',
  CONFERENCE_TABLE: 'Users',
  CABINET: 'ArchiveIcon',
  // Office Equipment
  PRINTER: 'Printer',
  SCANNER: 'Scanner',
  PROJECTOR: 'Projector',
  PHOTOCOPIER: 'Copy',
  // Facilities
  AIR_CONDITIONER: 'Wind',
  // Vehicles
  VEHICLE: 'Car',
  // Electrical
  ELECTRICAL_EQUIPMENT: 'Zap',
  // Other
  OTHER: 'Package',
  // Digital
  BRAND_ASSETS: 'Image',
  SALES_DOCUMENTS: 'FileText',
  LEGAL_COMPLIANCE: 'ShieldCheck',
  MARKETING: 'Megaphone',
  COMPANY_DOCUMENTS: 'FolderOpen',
}

const DOCUMENT_TYPE_LABELS: Record<AssetDocumentType, string> = {
  PURCHASE_INVOICE: 'Purchase Invoice',
  WARRANTY_CARD: 'Warranty Card',
  AMC: 'AMC',
  REPAIR_INVOICE: 'Repair Invoice',
  DELIVERY_CHALLAN: 'Delivery Challan',
  ASSET_PHOTOGRAPH: 'Asset Photograph',
  OTHER: 'Other Document',
}

const PHYSICAL_STATUS_LABELS: Record<PhysicalAssetStatus, string> = {
  AVAILABLE: 'Available',
  ASSIGNED: 'Assigned',
  UNDER_MAINTENANCE: 'Under Maintenance',
  DAMAGED: 'Damaged',
  LOST: 'Lost',
  RETIRED: 'Retired',
  DISPOSED: 'Disposed',
}

const DIGITAL_STATUS_LABELS: Record<DigitalAssetStatus, string> = {
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
  EXPIRED: 'Expired',
}

const STATUS_LABELS: Record<string, string> = {
  ...PHYSICAL_STATUS_LABELS,
  ...DIGITAL_STATUS_LABELS,
}

const PHYSICAL_STATUS_COLORS: Record<PhysicalAssetStatus, string> = {
  AVAILABLE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  ASSIGNED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  UNDER_MAINTENANCE: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  DAMAGED: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  LOST: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800',
  RETIRED: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300 border-slate-200 dark:border-slate-800',
  DISPOSED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 border-gray-200 dark:border-gray-800',
}

const DIGITAL_STATUS_COLORS: Record<DigitalAssetStatus, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  ARCHIVED: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300 border-slate-200 dark:border-slate-800',
  EXPIRED: 'bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive border-destructive/20 dark:border-destructive',
}

const STATUS_COLORS: Record<string, string> = {
  ...PHYSICAL_STATUS_COLORS,
  ...DIGITAL_STATUS_COLORS,
}

const WARRANTY_STATUS_LABELS: Record<WarrantyStatus, string> = {
  ACTIVE: 'Active',
  EXPIRING_SOON: 'Expiring Soon',
  EXPIRED: 'Expired',
  NO_WARRANTY: 'No Warranty',
}

const WARRANTY_STATUS_COLORS: Record<WarrantyStatus, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  EXPIRING_SOON: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  EXPIRED: 'bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive border-destructive/20 dark:border-destructive',
  NO_WARRANTY: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300 border-slate-200 dark:border-slate-800',
}

function normalizeAssets(rows: any[]): AssetOption[] {
  return rows.map((r) => ({
    ...r,
    id: r.id ?? r._id,
    warrantyStatus: r.warrantyStatus,
  })) as AssetOption[]
}

export function useAssets(filters?: {
  assetType?: AssetType
  category?: AssetCategory
  status?: AssetStatus
  fileType?: string
  search?: string
  assignedTo?: string
  department?: string
  location?: string
  warrantyStatus?: WarrantyStatus
  page?: number
  limit?: number
}) {
  const { organization } = useAuth()
  const { can } = usePermissions()

  return useQuery({
    queryKey: ['assets', organization?.id, filters],
    queryFn: async () => {
      const params: Record<string, any> = { organizationId: organization?.id }
      if (filters?.assetType) params.assetType = filters.assetType
      if (filters?.category) params.category = filters.category
      if (filters?.status) params.status = filters.status
      if (filters?.fileType) params.fileType = filters.fileType
      if (filters?.search) params.search = filters.search
      if (filters?.assignedTo) params.assignedTo = filters.assignedTo
      if (filters?.department) params.department = filters.department
      if (filters?.location) params.location = filters.location
      if (filters?.warrantyStatus) params.warrantyStatus = filters.warrantyStatus
      if (filters?.page) params.page = filters.page
      if (filters?.limit) params.limit = filters.limit

      const res = await api.get<any>('/assets', params)
      return {
        data: normalizeAssets(res?.data || []),
        pagination: res?.pagination,
      }
    },
    enabled: !!organization?.id && can('ASSETS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssetDetail(assetId: string | undefined) {
  const { organization } = useAuth()
  const { can } = usePermissions()

  return useQuery({
    queryKey: ['asset', assetId, organization?.id],
    queryFn: async () => {
      const res = await api.get<any>(`/assets/${assetId}`, { organizationId: organization?.id })
      return res
    },
    enabled: !!organization?.id && can('ASSETS', 'VIEW') && !!assetId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssetSummary(assetType?: AssetType) {
  const { organization } = useAuth()
  const { can } = usePermissions()

  return useQuery({
    queryKey: ['assets', 'summary', organization?.id, assetType],
    queryFn: async () => {
      const params: Record<string, any> = { organizationId: organization?.id }
      if (assetType) params.assetType = assetType
      const res = await api.get<any>('/assets/summary', params)
      return res
    },
    enabled: !!organization?.id && can('ASSETS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssetMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = ['assets', organization?.id]
  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const create = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      api.post<any>('/assets', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>
      api.patch<any>(`/assets/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/assets/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const replace = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>
      api.post<any>(`/assets/${id}/replace`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const assign = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>
      api.post<any>(`/assets/${id}/assign`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const returnAsset = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>
      api.post<any>(`/assets/${id}/return`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const transfer = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) =>
      api.post<any>(`/assets/${id}/transfer`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, remove, replace, assign, returnAsset, transfer }
}

export function useAssetHistory(assetId: string) {
  const { organization } = useAuth()
  const { can } = usePermissions()

  return useQuery({
    queryKey: ['asset-history', assetId, organization?.id],
    queryFn: async () => {
      const res = await api.get<any>(`/assets/${assetId}/history`, { organizationId: organization?.id })
      return res?.data || []
    },
    enabled: !!organization?.id && can('ASSETS', 'VIEW') && !!assetId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssetMaintenance(assetId: string) {
  const { organization } = useAuth()
  const { can } = usePermissions()

  return useQuery({
    queryKey: ['asset-maintenance', assetId, organization?.id],
    queryFn: async () => {
      const res = await api.get<any>(`/assets/${assetId}/maintenance`, { organizationId: organization?.id })
      return res?.data || []
    },
    enabled: !!organization?.id && can('ASSETS', 'VIEW') && !!assetId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssetMaintenanceMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = ['asset-maintenance', organization?.id]
  const invalidate = (assetId: string) => {
    queryClient.invalidateQueries({ queryKey: ['asset-maintenance', assetId, organization?.id] })
    queryClient.invalidateQueries({ queryKey: ['assets', organization?.id] })
  }

  const create = useMutation({
    mutationFn: ({ assetId, payload }: { assetId: string; payload: Record<string, any> }) =>
      api.post<any>(`/assets/${assetId}/maintenance`, { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: (_data, vars) => invalidate(vars.assetId),
  })

  const complete = useMutation({
    mutationFn: ({ assetId, maintenanceId, payload }: { assetId: string; maintenanceId: string; payload: Record<string, any> }) =>
      api.patch<any>(`/assets/${assetId}/maintenance/${maintenanceId}/complete`, payload, { skipErrorToast: true }),
    onSuccess: (_data, vars) => invalidate(vars.assetId),
  })

  const cancel = useMutation({
    mutationFn: ({ assetId, maintenanceId }: { assetId: string; maintenanceId: string }) =>
      api.delete<any>(`/assets/${assetId}/maintenance/${maintenanceId}`, { skipErrorToast: true }),
    onSuccess: (_data, vars) => invalidate(vars.assetId),
  })

  return { create, complete, cancel }
}

export function useAssetDocuments(assetId: string) {
  const { organization } = useAuth()
  const { can } = usePermissions()

  return useQuery({
    queryKey: ['asset-documents', assetId, organization?.id],
    queryFn: async () => {
      const res = await api.get<any>(`/assets/${assetId}/documents`, { organizationId: organization?.id })
      return res?.data || []
    },
    enabled: !!organization?.id && can('ASSETS', 'VIEW') && !!assetId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssetDocumentMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = (assetId: string) => {
    queryClient.invalidateQueries({ queryKey: ['asset-documents', assetId, organization?.id] })
    queryClient.invalidateQueries({ queryKey: ['assets', organization?.id] })
  }

  const add = useMutation({
    mutationFn: ({ assetId, payload }: { assetId: string; payload: Record<string, any> }) =>
      api.post<any>(`/assets/${assetId}/documents`, { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: (_data, vars) => invalidate(vars.assetId),
  })

  const remove = useMutation({
    mutationFn: ({ assetId, documentId }: { assetId: string; documentId: string }) =>
      api.delete<any>(`/assets/${assetId}/documents/${documentId}`, { skipErrorToast: true }),
    onSuccess: (_data, vars) => invalidate(vars.assetId),
  })

  return { add, remove }
}

export function useUsersForAssignment() {
  const { organization } = useAuth()
  const { can } = usePermissions()

  return useQuery({
    queryKey: ['users-for-assignment', organization?.id],
    queryFn: async () => {
      const res = await api.get<any>('/users', { organizationId: organization?.id, status: 'ACTIVE' })
      return (res?.data || []).map((u: any) => ({
        id: u.id ?? u._id,
        name: u.name || u.email,
        email: u.email,
        department: u.department,
      }))
    },
    enabled: !!organization?.id && can('USERS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export {
  PHYSICAL_CATEGORY_LABELS,
  DIGITAL_CATEGORY_LABELS,
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  PHYSICAL_STATUS_LABELS,
  DIGITAL_STATUS_LABELS,
  STATUS_LABELS,
  PHYSICAL_STATUS_COLORS,
  DIGITAL_STATUS_COLORS,
  STATUS_COLORS,
  WARRANTY_STATUS_LABELS,
  WARRANTY_STATUS_COLORS,
  DOCUMENT_TYPE_LABELS,
}